<template>
  <v-dialog :value="value" :max-width="showFinder ? '100%' : isRecurring ? '1080' : '440'" :fullscreen="showFinder" persistent>
    <v-card
      :dark="isDarkMode"
      class="wcd-card rounded-xl overflow-hidden d-flex flex-column"
      :class="{ 'light-theme': !isDarkMode }"
      :style="{
        transition: 'max-width 0.25s ease',
        height: showFinder ? '100vh' : 'auto',
        maxHeight: showFinder ? '100vh' : '90vh',
      }">
      <!-- The Header -->
      <div class="wcd-header">
        <div class="d-flex align-center" style="gap: 10px">
          <div class="wcd-icon" :style="{ background: data?.template.color || '#4CAF50' }">
            <v-icon small color="white">{{ localIcon || data?.template.icon || "mdi-calendar" }}</v-icon>
          </div>
          <div>
            <div class="wcd-title">{{ localTitle || data?.template.title }}</div>
            <div class="wcd-subtitle">{{ selectedEmployeeName }}</div>
          </div>
        </div>

        <v-btn icon x-small @click="$emit('input', false)" :style="{ color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.4)' }">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Content Container -->
      <div
        class="d-flex flex-grow-1 overflow-hidden"
        :class="$vuetify.breakpoint.smAndDown ? 'flex-column' : 'flex-row'"
        :style="{
          minHeight: showFinder ? '0' : '480px',
        }">
        <!-- Left Column: Booking Form -->
        <div
          class="wcd-form-column d-flex flex-column"
          :style="
            $vuetify.breakpoint.smAndDown
              ? 'width: 100%; max-height: calc(90vh - 75px);'
              : {
                  width: '440px',
                  maxWidth: '440px',
                  flexShrink: 0,
                  height: '100%',
                  maxHeight: showFinder ? '100%' : 'calc(90vh - 75px)',
                }
          ">
          <v-card-text class="pt-4 pb-2 flex-grow-1 overflow-y-auto" style="scrollbar-width: thin">
            <!-- Zeitbereich Row -->
            <div class="wcd-time-row">
              <div class="wcd-time-item">
                <div class="wcd-time-label">Start</div>
                <v-text-field
                  v-if="data"
                  v-model="localStart"
                  type="time"
                  dense
                  solo
                  flat
                  :dark="isDarkMode"
                  hide-details
                  :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                  class="wcd-time-input"></v-text-field>
              </div>
              <div class="wcd-time-item" v-if="data">
                <div class="wcd-time-label">Dauer</div>
                <v-text-field
                  v-model.number="localDuration"
                  type="number"
                  suffix="Min"
                  dense
                  solo
                  flat
                  :dark="isDarkMode"
                  hide-details
                  :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                  class="wcd-time-input"></v-text-field>
              </div>
              <div class="wcd-time-item">
                <div class="wcd-time-label">Ende</div>
                <div class="wcd-time-result">{{ calculatedEnd }}</div>
              </div>
            </div>

            <!-- Mitarbeiter Auswahl (Dropdown) -->
            <div class="mt-4" v-if="employees && employees.length > 0">
              <div class="wcd-time-label mb-1">Mitarbeiter *</div>
              <v-select
                v-model="localColId"
                :items="employeeItems"
                dense
                solo
                flat
                hide-details
                :dark="isDarkMode"
                :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                class="wcd-employee-select"></v-select>
            </div>

            <!-- Termin-Finder Toggle Button Section (Hidden on Mobile) -->
            <div class="mt-4 mb-2" v-if="!$vuetify.breakpoint.xs">
              <v-btn block depressed class="wcd-finder-toggle-bar text-none" :class="{ active: showFinder }" @click="toggleFinder">
                <v-icon left size="16">mdi-calendar-search</v-icon>
                <span>{{ showFinder ? "Termin-Finder schliessen" : "Freie Termine finden (Termin-Finder)" }}</span>
                <v-icon right size="16" class="ml-auto">{{ showFinder ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
              </v-btn>
            </div>

            <!-- Kunde Bereich (nur bei requiresCustomer) -->
            <div v-if="data?.template.requiresCustomer" class="mt-4 relative" style="z-index: 1000">
              <div class="wcd-time-label mb-1">Kunde *</div>

              <!-- Toggle: Account vorhanden / kein Account -->
              <div class="d-flex mb-3" style="gap: 4px">
                <div class="wcd-toggle-btn flex-grow-1" :class="{ active: !noAccountMode }" @click="noAccountMode = false">
                  <v-icon x-small class="mr-1">mdi-account-search</v-icon> Kunde suchen
                </div>
                <div
                  class="wcd-toggle-btn flex-grow-1"
                  :class="{ active: noAccountMode }"
                  @click="
                    noAccountMode = true
                    selectedCustomer = null
                  ">
                  <v-icon x-small class="mr-1">mdi-account-off</v-icon> Kein Account
                </div>
              </div>

              <!-- Modus 1: Normaler Kunden-Suche -->
              <template v-if="!noAccountMode">
                <user-search
                  :value="selectedCustomer"
                  @update="updateCustomer"
                  return-object
                  ignore-rights
                  label="Kunden suchen..."
                  :dark="isDarkMode"
                  outlined
                  dense
                  :menu-props="{ zIndex: 9999, offsetY: true }"></user-search>
              </template>

              <!-- Modus 2: Kein Account – Manuelle Eingabe -->
              <template v-else>
                <div class="d-flex gap-2">
                  <v-text-field v-model="noAccountInfo.firstName" label="Vorname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                  <v-text-field v-model="noAccountInfo.lastName" label="Nachname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                </div>
                <v-text-field
                  v-model="noAccountInfo.phone"
                  label="Telefon *"
                  outlined
                  dense
                  :dark="isDarkMode"
                  hide-details
                  class="mt-2"
                  prepend-inner-icon="mdi-phone"></v-text-field>
                <v-text-field
                  v-model="noAccountInfo.email"
                  label="E-Mail (Optional)"
                  outlined
                  dense
                  :dark="isDarkMode"
                  hide-details
                  class="mt-2"
                  prepend-inner-icon="mdi-email-outline"></v-text-field>
              </template>
            </div>

            <!-- Zweite Person hinzufügen (nur bei requiresCustomer) -->
            <div v-if="data?.template.requiresCustomer && (selectedCustomer || noAccountMode)" class="mt-3">
              <div v-if="!showSecondPerson" class="d-flex align-center">
                <v-btn text x-small color="primary" @click="addSecondPerson" class="text-none">
                  <v-icon left x-small>mdi-account-plus</v-icon> Zweite Person hinzufügen (+30 Min)
                </v-btn>
              </div>
              <div v-else>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="wcd-time-label">2. Person</div>
                  <v-btn icon x-small @click="removeSecondPerson" style="color: rgba(255, 255, 255, 0.3)">
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </div>

                <div class="d-flex mb-2" style="gap: 4px">
                  <div class="wcd-toggle-btn flex-grow-1" :class="{ active: !noAccountMode2 }" @click="noAccountMode2 = false">
                    <v-icon x-small class="mr-1">mdi-account-search</v-icon> Suchen
                  </div>
                  <div
                    class="wcd-toggle-btn flex-grow-1"
                    :class="{ active: noAccountMode2 }"
                    @click="
                      noAccountMode2 = true
                      selectedCustomer2 = null
                    ">
                    <v-icon x-small class="mr-1">mdi-account-off</v-icon> Kein Account
                  </div>
                </div>

                <template v-if="!noAccountMode2">
                  <user-search
                    :value="selectedCustomer2"
                    @update="updateCustomer2"
                    return-object
                    ignore-rights
                    label="2. Kunden suchen..."
                    :dark="isDarkMode"
                    outlined
                    dense
                    :menu-props="{ zIndex: 9999, offsetY: true }"></user-search>
                </template>
                <template v-else>
                  <div class="d-flex gap-2">
                    <v-text-field v-model="noAccountInfo2.firstName" label="Vorname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                    <v-text-field v-model="noAccountInfo2.lastName" label="Nachname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                  </div>
                  <v-text-field
                    v-model="noAccountInfo2.phone"
                    label="Telefon *"
                    outlined
                    dense
                    :dark="isDarkMode"
                    hide-details
                    class="mt-2"
                    prepend-inner-icon="mdi-phone"></v-text-field>
                  <v-text-field
                    v-model="noAccountInfo2.email"
                    label="E-Mail (Optional)"
                    outlined
                    dense
                    :dark="isDarkMode"
                    hide-details
                    class="mt-2"
                    prepend-inner-icon="mdi-email-outline"></v-text-field>
                </template>

                <div class="wcd-dual-badge mt-2">
                  <v-icon x-small color="primary" class="mr-1">mdi-account-multiple</v-icon>
                  <span>Dauer +30 Min ({{ localDuration }} Min total)</span>
                </div>
              </div>
            </div>

            <!-- New Contract Section -->
            <div v-if="data?.template.specialType === 'new_contract'" class="mt-4 relative" style="z-index: 1000">
              <div class="wcd-time-label mb-1">Kunde *</div>

              <!-- Toggle: Neuer Kunde / Bestehender Kunde -->
              <div class="d-flex mb-3" style="gap: 4px">
                <div
                  class="wcd-toggle-btn flex-grow-1"
                  :class="{ active: contractMode === 'guest' }"
                  @click="
                    contractMode = 'guest'
                    selectedCustomer = null
                  ">
                  <v-icon x-small class="mr-1">mdi-account-plus</v-icon> Neuer Kunde (Gast)
                </div>
                <div
                  class="wcd-toggle-btn flex-grow-1"
                  :class="{ active: contractMode === 'existing' }"
                  @click="
                    contractMode = 'existing'
                    guestData = { firstName: '', lastName: '', email: '' }
                  ">
                  <v-icon x-small class="mr-1">mdi-account-search</v-icon> Bestehender Kunde
                </div>
              </div>

              <!-- Modus 1: Neuer Kunde (Gast) -->
              <template v-if="contractMode === 'guest'">
                <div class="d-flex gap-2">
                  <v-text-field v-model="guestData.firstName" label="Vorname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                  <v-text-field v-model="guestData.lastName" label="Nachname *" outlined dense :dark="isDarkMode" hide-details></v-text-field>
                </div>
                <v-text-field
                  v-model="guestData.email"
                  label="E-Mail (Optional)"
                  outlined
                  dense
                  :dark="isDarkMode"
                  hide-details
                  class="mt-2"
                  prepend-inner-icon="mdi-email-outline"></v-text-field>
              </template>

              <!-- Modus 2: Bestehender Kunde -->
              <template v-else>
                <user-search
                  :value="selectedCustomer"
                  @update="updateCustomer"
                  return-object
                  ignore-rights
                  label="Kunden suchen..."
                  :dark="isDarkMode"
                  outlined
                  dense
                  :menu-props="{ zIndex: 9999, offsetY: true }"></user-search>
              </template>
            </div>

            <!-- Titel-Eingabe falls freier Termin (ap1) -->
            <div v-if="data?.template?.id === 'ap1'" class="mt-4">
              <div class="wcd-time-label mb-1">Titel *</div>
              <v-text-field v-model="localTitle" outlined dense :dark="isDarkMode" hide-details placeholder="Termintitel eingeben..."></v-text-field>
            </div>

            <!-- Icon-Auswahl falls freier Termin (ap1) -->
            <div v-if="data?.template?.id === 'ap1'" class="mt-3">
              <div class="wcd-time-label mb-2">Icon</div>
              <div class="d-flex flex-wrap gap-2">
                <v-btn
                  v-for="ic in ['mdi-calendar', 'mdi-account-group', 'mdi-phone', 'mdi-laptop', 'mdi-dumbbell', 'mdi-heart-pulse']"
                  :key="ic"
                  icon
                  small
                  :color="localIcon === ic ? 'primary' : 'grey'"
                  style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1)"
                  :style="localIcon === ic ? 'border-color: var(--v-primary-base)' : ''"
                  @click="localIcon = ic">
                  <v-icon small>{{ ic }}</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Serien-Termin / Wiederholung -->
            <div class="mt-4" v-if="data">
              <v-divider class="mb-4" style="opacity: 0.05"></v-divider>
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="wcd-time-label mb-0" style="font-size: 0.7rem; letter-spacing: 0.5px">Termin wiederholen (Serie)</div>
                <v-switch v-model="isRecurring" dense hide-details class="mt-0 pt-0" color="primary"></v-switch>
              </div>

              <v-expand-transition>
                <div v-if="isRecurring" class="wcd-recurring-panel pa-3 rounded-lg mt-2 mb-2">
                  <!-- Frequenz -->
                  <div class="mb-3">
                    <div class="wcd-small-label">Frequenz</div>
                    <v-btn-toggle v-model="recurrence.frequency" mandatory dense class="wcd-toggle-group">
                      <v-btn value="daily" class="wcd-toggle-btn-small">Täglich</v-btn>
                      <v-btn value="weekly" class="wcd-toggle-btn-small">Wöchentlich</v-btn>
                      <v-btn value="monthly" class="wcd-toggle-btn-small">Monatlich</v-btn>
                    </v-btn-toggle>
                  </div>

                  <!-- Intervall-Zahl -->
                  <div class="d-flex align-center mb-3" style="gap: 12px">
                    <div style="width: 80px">
                      <div class="wcd-small-label">Intervall</div>
                      <v-text-field
                        v-model.number="recurrence.interval"
                        type="number"
                        min="1"
                        dense
                        solo
                        flat
                        hide-details
                        :dark="isDarkMode"
                        :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                        class="wcd-numeric-input"></v-text-field>
                    </div>
                    <div class="flex-grow-1 pt-4" style="font-size: 0.75rem; font-weight: 600; opacity: 0.7">
                      Alle {{ recurrence.interval || 1 }}
                      {{ recurrence.frequency === "daily" ? "Tag(e)" : recurrence.frequency === "weekly" ? "Woche(n)" : "Monat(e)" }}
                    </div>
                  </div>

                  <!-- Wochentage (nur bei weekly) -->
                  <div v-if="recurrence.frequency === 'weekly'" class="mb-3">
                    <div class="wcd-small-label mb-1">An folgenden Tagen</div>
                    <div class="d-flex justify-space-between" style="gap: 4px">
                      <v-btn
                        v-for="(day, idx) in weekdayOptions"
                        :key="idx"
                        x-small
                        fab
                        depressed
                        :color="recurrence.weekdays.includes(day.value) ? 'primary' : isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'"
                        :class="
                          recurrence.weekdays.includes(day.value)
                            ? 'white--text font-weight-bold'
                            : isDarkMode
                            ? 'grey--text text--lighten-1'
                            : 'grey--text text--darken-2'
                        "
                        @click="toggleWeekday(day.value)"
                        style="width: 28px; height: 28px; min-width: 28px; font-size: 0.7rem">
                        {{ day.text }}
                      </v-btn>
                    </div>
                  </div>

                  <!-- Seriendauer / Ende -->
                  <div class="mb-2">
                    <div class="wcd-small-label mb-2">Seriendauer</div>

                    <v-radio-group v-model="recurrenceEndType" dense hide-details class="mt-0 pt-0 wcd-radio-group">
                      <div class="d-flex flex-column gap-2">
                        <!-- Nach X Terminen -->
                        <div class="d-flex align-center" style="gap: 8px">
                          <v-radio value="count" color="primary" dense class="mr-0"></v-radio>
                          <span style="font-size: 0.75rem; opacity: 0.8">Endet nach</span>
                          <v-text-field
                            v-model.number="recurrence.count"
                            :disabled="recurrenceEndType !== 'count'"
                            type="number"
                            min="1"
                            dense
                            solo
                            flat
                            hide-details
                            :dark="isDarkMode"
                            :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                            style="width: 60px; max-width: 60px"
                            class="wcd-numeric-input"></v-text-field>
                          <span style="font-size: 0.75rem; opacity: 0.8">Terminen</span>
                        </div>

                        <!-- Am Enddatum -->
                        <div class="d-flex align-center mt-1" style="gap: 8px">
                          <v-radio value="date" color="primary" dense class="mr-0"></v-radio>
                          <span style="font-size: 0.75rem; opacity: 0.8">Endet am</span>

                          <v-menu v-model="showDatePicker" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                :value="formatDate(recurrence.endDate)"
                                :disabled="recurrenceEndType !== 'date'"
                                readonly
                                dense
                                solo
                                flat
                                hide-details
                                v-bind="attrs"
                                v-on="on"
                                :dark="isDarkMode"
                                :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                                style="width: 120px"
                                class="wcd-date-input">
                                <template v-slot:append>
                                  <v-icon small :disabled="recurrenceEndType !== 'date'">mdi-calendar</v-icon>
                                </template>
                              </v-text-field>
                            </template>
                            <v-date-picker v-model="recurrence.endDate" @input="showDatePicker = false" no-title scrollable></v-date-picker>
                          </v-menu>
                        </div>
                      </div>
                    </v-radio-group>
                  </div>

                  <!-- Uhrzeit-Flexibilität -->
                  <div class="mt-3">
                    <div class="wcd-small-label mb-1">Uhrzeit-Flexibilität</div>
                    <v-select
                      v-model="recurrenceFlexibility"
                      :items="flexibilityOptions"
                      dense
                      solo
                      flat
                      hide-details
                      :dark="isDarkMode"
                      :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                      class="wcd-employee-select"></v-select>

                    <v-expand-transition>
                      <div v-if="recurrenceFlexibility === 'window'" class="d-flex align-center mt-2" style="gap: 8px">
                        <span style="font-size: 0.75rem; opacity: 0.8">Bereich: ±</span>
                        <v-text-field
                          v-model.number="recurrenceFlexibilityWindow"
                          type="number"
                          min="1"
                          max="12"
                          dense
                          solo
                          flat
                          hide-details
                          :dark="isDarkMode"
                          :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                          style="width: 60px; max-width: 60px"
                          class="wcd-numeric-input"></v-text-field>
                        <span style="font-size: 0.75rem; opacity: 0.8">Stunden</span>
                      </div>
                    </v-expand-transition>
                  </div>
                </div>
              </v-expand-transition>
            </div>

            <!-- Notes Textarea -->
            <v-textarea
              v-if="data"
              v-model="localDesc"
              :label="noteLabel"
              outlined
              dense
              rows="2"
              :dark="isDarkMode"
              :hide-details="!isNoteRequired || !!localDesc"
              :error="isNoteRequired && !localDesc"
              :error-messages="isNoteRequired && !localDesc ? ['Pflichtfeld – bitte Notiz eingeben'] : []"
              class="mt-4"
              style="font-size: 0.85rem"></v-textarea>
          </v-card-text>

          <v-divider style="opacity: 0.05"></v-divider>

          <!-- Left Card Actions -->
          <v-card-actions class="px-5 pb-4 pt-4">
            <v-spacer></v-spacer>
            <v-btn
              text
              small
              @click="$emit('input', false)"
              :style="{ color: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.5)', textTransform: 'none' }"
              >Abbrechen</v-btn
            >
            <v-btn small depressed class="wcd-save-btn" @click="save" :disabled="!canSave"> <v-icon left x-small>mdi-check</v-icon> Erstellen </v-btn>
          </v-card-actions>
        </div>

        <!-- Right Column: Recurrence Preview Panel -->
        <div
          v-if="isRecurring && !showFinder"
          class="wcd-preview-column d-flex flex-column flex-grow-1"
          :style="
            $vuetify.breakpoint.smAndDown
              ? 'width: 100%; border-top: 1px solid rgba(255,255,255,0.08);'
              : {
                  borderLeft: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.01)' : 'rgba(0, 0, 0, 0.01)',
                  height: '100%',
                  minWidth: '560px',
                  maxWidth: '640px',
                  maxHeight: showFinder ? '100%' : 'calc(90vh - 75px)',
                }
          ">
          <!-- Preview Header / Controls -->
          <div class="pa-4 d-flex flex-column" style="gap: 12px; border-bottom: 1px solid rgba(255, 255, 255, 0.05)">
            <div class="d-flex align-center justify-space-between">
              <div class="wcd-title" style="font-size: 0.95rem">Serien-Vorschau</div>
              <v-progress-circular v-if="loadingPreview" indeterminate size="16" width="2" color="primary"></v-progress-circular>
            </div>

            <!-- Global Conflict Rule Selector -->
            <div>
              <div class="wcd-small-label mb-1">Konflikt-Regel (Serientermine)</div>
              <v-select
                v-model="globalConflictRule"
                :items="conflictRules"
                dense
                solo
                flat
                hide-details
                :dark="isDarkMode"
                :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
                class="wcd-employee-select"></v-select>
            </div>
          </div>

          <!-- Preview Items List -->
          <div class="flex-grow-1 overflow-y-auto pa-4" style="scrollbar-width: thin; max-height: calc(90vh - 200px)">
            <div v-if="!previewList || !previewList.length" class="text-center py-8 grey--text text--darken-1" style="font-size: 0.85rem">
              Keine Vorschau verfügbar. Bitte überprüfen Sie die Frequenz- und Datumseinstellungen.
            </div>

            <div v-else class="d-flex flex-column" style="gap: 10px">
              <div
                v-for="(item, idx) in previewList"
                :key="idx"
                class="wcd-preview-item pa-3 rounded-lg d-flex align-center justify-space-between"
                :class="{
                  skipped: item.skipped,
                  'light-theme-item': !isDarkMode,
                }"
                :style="getPreviewItemStyle(item)">
                <!-- Left: Date & Day & Status -->
                <div class="d-flex flex-column" style="gap: 2px; max-width: 320px">
                  <div class="d-flex align-center" style="gap: 6px">
                    <v-checkbox v-model="item.skipped" dense hide-details class="mt-0 pt-0 mr-1" color="error" style="margin-top: 0px"></v-checkbox>
                    <span
                      class="font-weight-bold"
                      style="font-size: 0.8rem"
                      :style="{ textDecoration: item.skipped ? 'line-through' : 'none', opacity: item.skipped ? 0.5 : 1 }">
                      {{ formatPreviewDate(item.date) }}
                    </span>
                  </div>

                  <!-- Status Chip / Text -->
                  <div v-if="!item.skipped" class="d-flex align-center mt-1" style="gap: 4px; flex-wrap: wrap">
                    <template v-if="item.isAdjusted">
                      <v-icon x-small color="success">mdi-clock-outline</v-icon>
                      <span class="success--text font-weight-bold" style="font-size: 0.7rem">
                        Verschoben: {{ item.adjustedTime }} (statt {{ item.originalTime }})
                      </span>
                    </template>
                    <template v-else-if="item.status === 'available'">
                      <v-icon x-small color="success">mdi-check-circle</v-icon>
                      <span class="success--text font-weight-medium" style="font-size: 0.7rem">Verfügbar</span>
                    </template>
                    <template v-else-if="item.status === 'collision'">
                      <v-icon x-small color="warning">mdi-alert-circle</v-icon>
                      <span
                        class="warning--text font-weight-medium"
                        style="font-size: 0.7rem; cursor: help"
                        :title="getCollisionTooltip(item.conflict)">
                        Kollision: {{ item.conflict?.title || "Eintrag" }}
                      </span>
                    </template>
                    <template v-else-if="item.status === 'no_shiftplan'">
                      <v-icon x-small color="warning">mdi-calendar-blank</v-icon>
                      <span class="warning--text font-weight-medium" style="font-size: 0.7rem">Schichtplan fehlt</span>
                    </template>
                    <template v-else-if="item.status === 'absent'">
                      <v-icon x-small color="error">mdi-close-circle</v-icon>
                      <span class="error--text font-weight-medium" style="font-size: 0.7rem">Abwesend</span>
                    </template>
                  </div>
                  <div v-else class="grey--text font-weight-medium" style="font-size: 0.7rem; opacity: 0.6">Übersprungen</div>
                </div>

                <!-- Right: Employee & Time Selector -->
                <div class="d-flex align-center" style="gap: 8px" v-if="!item.skipped">
                  <!-- Time Select -->
                  <div style="width: 100px">
                    <v-select
                      :value="item.adjustedTime || item.originalTime"
                      :items="getAvailableTimeOptions(item)"
                      @change="onPreviewTimeChange(item, $event)"
                      dense
                      solo
                      flat
                      hide-details
                      :dark="isDarkMode"
                      :background-color="isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'"
                      style="font-size: 0.75rem"
                      class="wcd-preview-time-select"></v-select>
                  </div>

                  <!-- Employee Select -->
                  <div style="width: 240px">
                    <v-select
                      v-model="item.selectedEmployeeId"
                      :items="getPreviewEmployeeOptions(item)"
                      @change="onPreviewEmployeeChange(item)"
                      dense
                      solo
                      flat
                      hide-details
                      :dark="isDarkMode"
                      :background-color="isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'"
                      class="wcd-preview-employee-select"
                      style="font-size: 0.75rem">
                      <template v-slot:selection="{ item }">
                        <span v-if="item" style="font-size: 0.75rem" :class="item.class">{{ item.text }}</span>
                      </template>
                      <template v-slot:item="{ item }">
                        <span v-if="item" style="font-size: 0.75rem" :class="item.class">{{ item.text }}</span>
                      </template>
                    </v-select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Finder Dashboard (Desktop only) -->
        <work-calendar-appointment-finder
          v-if="showFinder && !$vuetify.breakpoint.smAndDown"
          class="wcd-finder-section flex-grow-1"
          :style="{
            borderLeft: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
            background: isDarkMode ? 'rgba(255, 255, 255, 0.01)' : 'rgba(0, 0, 0, 0.01)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }"
          :employees="employees"
          :date="selectedDate"
          @update:date="selectedDate = $event"
          :start="localStart"
          @update:start="localStart = $event"
          :col-id="localColId"
          @update:col-id="localColId = $event"
          :duration="localDuration"
          :is-dark-mode="isDarkMode"
          @select-and-close="showFinder = false" />
      </div>

      <!-- Mobile Expandable Finder Section (Tablet only, disabled on Mobile Phone) -->
      <v-expand-transition v-if="$vuetify.breakpoint.smAndDown && !$vuetify.breakpoint.xs">
        <work-calendar-appointment-finder
          v-if="showFinder"
          class="wcd-mobile-finder-section pa-3"
          style="
            border-top: 1px solid rgba(255, 255, 255, 0.08);
            background: rgba(255, 255, 255, 0.01);
            height: 450px;
            display: flex;
            flex-direction: column;
          "
          :employees="employees"
          :date="selectedDate"
          @update:date="selectedDate = $event"
          :start="localStart"
          @update:start="localStart = $event"
          :col-id="localColId"
          @update:col-id="localColId = $event"
          :duration="localDuration"
          :is-dark-mode="isDarkMode"
          @select-and-close="showFinder = false" />
      </v-expand-transition>
    </v-card>
  </v-dialog>
</template>

<script>
import UserSearch from "@/components/userSearch"
import WorkCalendarAppointmentFinder from "./WorkCalendarAppointmentFinder"
import moment from "moment"
import api from "@/api"

export default {
  name: "WorkCalendarDropDialog",
  components: { UserSearch, WorkCalendarAppointmentFinder },
  props: {
    value: { type: Boolean, default: false },
    data: { type: Object, default: null },
    employees: { type: Array, default: () => [] },
    currentDate: { type: String, default: "" },
    isDarkMode: { type: Boolean, default: true },
  },
  data() {
    return {
      localColId: null,
      localStart: "",
      localDuration: 60,
      localDesc: "",
      localTitle: "",
      localIcon: "",
      selectedCustomer: null,
      guestData: {
        firstName: "",
        lastName: "",
        email: "",
      },
      noAccountMode: false,
      noAccountInfo: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      },
      showSecondPerson: false,
      selectedCustomer2: null,
      noAccountMode2: false,
      noAccountInfo2: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      },
      baseDuration: 60,
      contractMode: "guest",

      showFinder: false,
      selectedDate: null,

      // Recurrence (Series) Properties
      isRecurring: false,
      recurrenceEndType: "count",
      recurrence: {
        frequency: "daily",
        interval: 1,
        weekdays: [],
        endDate: "",
        count: 10,
      },
      showDatePicker: false,
      weekdayOptions: [
        { text: "Mo", value: 1 },
        { text: "Di", value: 2 },
        { text: "Mi", value: 3 },
        { text: "Do", value: 4 },
        { text: "Fr", value: 5 },
        { text: "Sa", value: 6 },
        { text: "So", value: 7 },
      ],
      // Preview fields
      previewList: [],
      loadingPreview: false,
      globalConflictRule: "same",
      recurrenceFlexibility: "fixed",
      recurrenceFlexibilityWindow: 2,
      flexibilityOptions: [
        { text: "Feste Uhrzeit", value: "fixed" },
        { text: "Beliebige Uhrzeit (Egal)", value: "any" },
        { text: "Flexibel (± Stunden)", value: "window" },
      ],
    }
  },
  watch: {
    data: {
      immediate: true,
      handler(val) {
        if (val) {
          this.localColId = val.col?.id || null
          this.localStart = val.time
          this.baseDuration = val.template.duration || 60
          this.localDuration = this.baseDuration
          this.localDesc = val.description || ""
          this.localTitle = val.template.title || ""
          this.localIcon = val.template.icon || "mdi-calendar"

          // Pre-fill customer/account info if extending or editing
          this.selectedCustomer = val.customer || null
          this.selectedCustomer2 = val.customer2 || null
          this.noAccountMode = !!val.noAccountData
          this.noAccountMode2 = !!val.noAccountData2
          this.showSecondPerson = !!val.customer2 || !!val.noAccountData2
          this.noAccountInfo = val.noAccountData ? { ...val.noAccountData } : { firstName: "", lastName: "", phone: "", email: "" }
          this.noAccountInfo2 = val.noAccountData2 ? { ...val.noAccountData2 } : { firstName: "", lastName: "", phone: "", email: "" }
          this.guestData = val.guestData ? { ...val.guestData } : { firstName: "", lastName: "", email: "" }
          this.contractMode = val.guestData ? "guest" : "existing"

          this.selectedDate = val.date || this.currentDate || moment().format("YYYY-MM-DD")
          this.showFinder = false

          // Reset Recurrence Defaults
          this.isRecurring = !!val.isExtend
          this.recurrenceEndType = "count"
          const initialDate = this.selectedDate
          this.recurrence = {
            frequency: "daily",
            interval: 1,
            weekdays: [moment(initialDate).isoWeekday()],
            endDate: moment(initialDate).add(1, "month").format("YYYY-MM-DD"),
            count: 10,
          }
          this.recurrenceFlexibility = "fixed"
          this.recurrenceFlexibilityWindow = 2
        }
      },
    },
    value(val) {
      if (val) {
        this.selectedDate = this.data?.date || this.currentDate || moment().format("YYYY-MM-DD")
        this.showFinder = false
      }
    },
    isRecurring(val) {
      if (val) {
        this.fetchPreviewDebounced()
      } else {
        this.previewList = []
      }
    },
    localStart() {
      this.fetchPreviewDebounced()
    },
    localDuration() {
      this.fetchPreviewDebounced()
    },
    localColId() {
      this.fetchPreviewDebounced()
    },
    selectedDate() {
      this.fetchPreviewDebounced()
    },
    recurrenceEndType() {
      this.fetchPreviewDebounced()
    },
    "recurrence.frequency"() {
      this.fetchPreviewDebounced()
    },
    "recurrence.interval"() {
      this.fetchPreviewDebounced()
    },
    "recurrence.endDate"() {
      this.fetchPreviewDebounced()
    },
    "recurrence.count"() {
      this.fetchPreviewDebounced()
    },
    "recurrence.weekdays": {
      deep: true,
      handler() {
        this.fetchPreviewDebounced()
      },
    },
    globalConflictRule() {
      this.applyGlobalConflictRule()
    },
    recurrenceFlexibility() {
      this.fetchPreviewDebounced()
    },
    recurrenceFlexibilityWindow() {
      this.fetchPreviewDebounced()
    },
  },
  computed: {
    calculatedEnd() {
      if (!this.localStart) return "-"
      return moment(this.localStart, "HH:mm").add(this.localDuration, "minutes").format("HH:mm")
    },
    employeeItems() {
      if (!this.employees) return []
      return this.employees.map((emp) => ({
        text: emp.name || emp.firstName || "Mitarbeiter",
        value: emp.id,
      }))
    },
    isTouchDevice() {
      if (typeof window === "undefined") return false
      return "ontouchstart" in window || navigator.maxTouchPoints > 0
    },
    selectedEmployeeName() {
      if (!this.employees || !this.localColId) return this.data?.col?.firstName || "Mitarbeiter"
      const emp = this.employees.find((e) => e.id === this.localColId)
      return emp ? emp.name : this.data?.col?.firstName || "Mitarbeiter"
    },
    canSave() {
      // 1. General validations (Customer, special types, notes, titles)
      if (this.data?.template.requiresCustomer) {
        const hasCustomer1 = this.noAccountMode
          ? this.noAccountInfo.firstName && this.noAccountInfo.lastName && this.noAccountInfo.phone
          : !!this.selectedCustomer
        if (!hasCustomer1) return false

        if (this.showSecondPerson) {
          const hasCustomer2 = this.noAccountMode2
            ? this.noAccountInfo2.firstName && this.noAccountInfo2.lastName && this.noAccountInfo2.phone
            : !!this.selectedCustomer2
          if (!hasCustomer2) return false
        }
      }

      if (this.data?.template.specialType === "new_contract") {
        if (this.contractMode === "guest" && (!this.guestData.firstName || !this.guestData.lastName)) {
          return false
        }
        if (this.contractMode === "existing" && !this.selectedCustomer) {
          return false
        }
      }

      if (this.data?.template?.id === "ap1" && !this.localTitle.trim()) {
        return false
      }

      if (this.isNoteRequired && !this.localDesc.trim()) {
        return false
      }

      // 2. Mode-specific validations (Single vs Recurring)
      if (this.isRecurring) {
        if (this.loadingPreview || !this.previewList || !this.previewList.length) {
          return false
        }
        const nonSkippedItems = this.previewList.filter((item) => !item.skipped)
        if (nonSkippedItems.length === 0) return false

        for (const item of nonSkippedItems) {
          const empId = item.selectedEmployeeId
          if (!empId || empId === "unassigned" || empId === "none") {
            return false
          }
          const effTime = item.adjustedTime || item.originalTime || this.localStart
          if (!effTime) {
            return false
          }
          if (item.status === "collision" || item.status === "absent" || item.status === "no_shiftplan") {
            return false
          }
        }
      } else {
        if (!this.localColId) {
          return false
        }
      }

      return true
    },
    isNoteRequired() {
      return !!this.data?.template?.requiresNote
    },
    noteLabel() {
      return this.isNoteRequired ? "Notiz (Pflichtfeld) *" : "Notiz (Optional)"
    },
    conflictRules() {
      return [
        { text: "Immer die gleiche Person", value: "same" },
        { text: "Automatisch zuteilen", value: "auto" },
        { text: "Personen selbst zuteilen", value: "helper" },
      ]
    },
  },
  methods: {
    fetchPreviewDebounced() {
      if (!this.isRecurring) {
        this.previewList = []
        return
      }
      if (this.previewTimeout) {
        clearTimeout(this.previewTimeout)
      }
      this.previewTimeout = setTimeout(() => {
        this.fetchPreview()
      }, 400)
    },
    async fetchPreview() {
      if (!this.isRecurring) return
      this.loadingPreview = true
      try {
        const payload = {
          date: this.selectedDate || this.data?.date || this.currentDate,
          start: this.localStart,
          end: this.calculatedEnd,
          duration: this.localDuration,
          assignedEmployee: this.localColId,
          recurrence: {
            frequency: this.recurrence.frequency,
            interval: this.recurrence.interval,
            weekdays: this.recurrence.frequency === "weekly" ? this.recurrence.weekdays : [],
            endDate: this.recurrenceEndType === "date" ? this.recurrence.endDate : null,
            count: this.recurrenceEndType === "count" ? this.recurrence.count : null,
            flexibilityType: this.recurrenceFlexibility,
            flexibilityWindow: this.recurrenceFlexibilityWindow,
          },
          type: this.data?.template?.type === "task" ? "task" : "appointment",
        }
        const data = await api.shiftManagement.fetchSeriesPreview(payload)
        const primaryIdStr = this.localColId ? String(this.localColId) : ""
        this.previewList = (data || []).map((item) => {
          const assignedIdStr = item.assignedEmployee?.id ? String(item.assignedEmployee.id) : primaryIdStr
          return {
            ...item,
            selectedEmployeeId: assignedIdStr,
            skipped: false,
            backendAdjustedTime: item.adjustedTime,
            backendStatus: item.status,
            originalAdjustedTime: item.adjustedTime,
          }
        })
        this.applyGlobalConflictRule()
      } catch (e) {
        console.error("Error fetching preview:", e)
      } finally {
        this.loadingPreview = false
      }
    },
    applyGlobalConflictRule() {
      if (!this.previewList || !this.previewList.length) return
      const primaryIdStr = this.localColId ? String(this.localColId) : ""
      this.previewList.forEach((item) => {
        if (this.globalConflictRule === "same") {
          this.$set(item, "selectedEmployeeId", primaryIdStr)
          this.$set(item, "adjustedTime", item.originalAdjustedTime !== undefined ? item.originalAdjustedTime : item.backendAdjustedTime)
          this.$set(item, "isAdjusted", !!item.adjustedTime)
          this.$set(item, "status", item.backendStatus || "available")
          this.$set(item, "skipped", false)
        } else if (this.globalConflictRule === "auto") {
          if (item.status === "available" && item.selectedEmployeeId === primaryIdStr) {
            // Keep as is
          } else if (item.backendStatus === "available") {
            this.$set(item, "selectedEmployeeId", primaryIdStr)
            this.$set(item, "adjustedTime", item.originalAdjustedTime !== undefined ? item.originalAdjustedTime : item.backendAdjustedTime)
            this.$set(item, "isAdjusted", !!item.adjustedTime)
            this.$set(item, "status", "available")
            this.$set(item, "skipped", false)
          } else {
            // Find the first alternative that is valid for auto-assignment under global flexibility rules
            const alt = item.alternatives ? item.alternatives.find((a) => a.validForAutoAssign) : null
            if (alt) {
              this.$set(item, "selectedEmployeeId", String(alt.id))
              this.$set(item, "adjustedTime", alt.adjustedTime || null)
              this.$set(item, "isAdjusted", !!alt.adjustedTime)
              this.$set(item, "status", "available")
              this.$set(item, "skipped", false)
            } else {
              this.$set(item, "selectedEmployeeId", primaryIdStr)
              this.$set(item, "adjustedTime", item.originalAdjustedTime !== undefined ? item.originalAdjustedTime : item.backendAdjustedTime)
              this.$set(item, "isAdjusted", !!item.adjustedTime)
              this.$set(item, "status", item.backendStatus || "available")
              this.$set(item, "skipped", false)
            }
          }
        } else if (this.globalConflictRule === "skip") {
          if (item.backendStatus === "available") {
            this.$set(item, "selectedEmployeeId", primaryIdStr)
            this.$set(item, "adjustedTime", item.originalAdjustedTime !== undefined ? item.originalAdjustedTime : item.backendAdjustedTime)
            this.$set(item, "isAdjusted", !!item.adjustedTime)
            this.$set(item, "status", "available")
            this.$set(item, "skipped", false)
          } else {
            this.$set(item, "skipped", true)
          }
        } else if (this.globalConflictRule === "helper") {
          if (item.backendStatus === "available") {
            this.$set(item, "selectedEmployeeId", primaryIdStr)
            this.$set(item, "adjustedTime", item.originalAdjustedTime !== undefined ? item.originalAdjustedTime : item.backendAdjustedTime)
            this.$set(item, "isAdjusted", !!item.adjustedTime)
            this.$set(item, "status", "available")
            this.$set(item, "skipped", false)
          } else {
            this.$set(item, "selectedEmployeeId", "unassigned")
            this.$set(item, "adjustedTime", null)
            this.$set(item, "isAdjusted", false)
            this.$set(item, "status", "available")
            this.$set(item, "skipped", false)
          }
        }
      })
    },
    onPreviewEmployeeChange(item) {
      const selectedId = item.selectedEmployeeId
      const isHelper = selectedId === "ma_info" || selectedId === "no_show" || selectedId === "unassigned"
      if (isHelper) {
        this.$set(item, "adjustedTime", null)
        this.$set(item, "isAdjusted", false)
        this.$set(item, "status", "available")
        return
      }

      const primaryIdStr = this.localColId ? String(this.localColId) : ""
      if (selectedId === primaryIdStr) {
        this.$set(item, "adjustedTime", item.originalAdjustedTime !== undefined ? item.originalAdjustedTime : item.backendAdjustedTime)
        this.$set(item, "isAdjusted", !!item.adjustedTime)
        this.$set(item, "status", item.backendStatus || "available")
        return
      }

      const alt = item.alternatives ? item.alternatives.find((a) => String(a.id) === selectedId) : null
      if (alt) {
        const bestTime = alt.adjustedTime || null
        this.$set(item, "adjustedTime", bestTime)
        this.$set(item, "isAdjusted", !!bestTime)
        this.$set(item, "status", "available")
      } else {
        this.$set(item, "adjustedTime", null)
        this.$set(item, "isAdjusted", false)
        this.$set(item, "status", item.backendStatus === "no_shiftplan" ? "no_shiftplan" : "collision")
      }
    },
    onPreviewTimeChange(item, newTime) {
      if (newTime === item.originalTime) {
        this.$set(item, "adjustedTime", null)
        this.$set(item, "isAdjusted", false)
      } else {
        this.$set(item, "adjustedTime", newTime)
        this.$set(item, "isAdjusted", true)
      }

      const selectedId = item.selectedEmployeeId
      const isHelper = selectedId === "ma_info" || selectedId === "no_show" || selectedId === "unassigned"
      if (isHelper) {
        this.$set(item, "status", "available")
        return
      }

      let slots = []
      const primaryIdStr = this.localColId ? String(this.localColId) : ""
      if (selectedId === primaryIdStr) {
        slots = item.freeSlots || []
      } else {
        const alt = item.alternatives ? item.alternatives.find((a) => String(a.id) === selectedId) : null
        if (alt) {
          slots = alt.availableSlots || []
        }
      }

      if (slots.includes(newTime)) {
        this.$set(item, "status", "available")
      } else if (newTime === item.originalTime && selectedId === primaryIdStr) {
        this.$set(item, "status", item.backendStatus || "available")
      } else {
        this.$set(item, "status", item.backendStatus === "no_shiftplan" ? "no_shiftplan" : "collision")
      }
    },
    getAvailableTimeOptions(item) {
      const selectedId = item.selectedEmployeeId
      const isHelper = selectedId === "ma_info" || selectedId === "no_show" || selectedId === "unassigned"
      if (isHelper) {
        return [item.originalTime]
      }

      let slots = []
      const primaryIdStr = this.localColId ? String(this.localColId) : ""
      if (selectedId === primaryIdStr) {
        slots = item.freeSlots || []
      } else {
        const alt = item.alternatives ? item.alternatives.find((a) => String(a.id) === selectedId) : null
        if (alt) {
          slots = alt.availableSlots || []
        }
      }

      const list = [...slots]
      if (!list.includes(item.originalTime)) {
        list.push(item.originalTime)
      }
      list.sort()
      return list
    },
    getPreviewEmployeeOptions(item) {
      if (!this.employees) return []
      return this.employees.map((emp) => {
        const empIdStr = emp.id ? String(emp.id) : ""
        const isHelper = empIdStr === "ma_info" || empIdStr === "no_show" || empIdStr === "unassigned"
        let suffix = ""
        let textClass = ""

        const assignedIdStr = item.assignedEmployee?.id ? String(item.assignedEmployee.id) : ""

        if (isHelper) {
          suffix = ""
          textClass = "primary--text font-weight-medium"
        } else if (assignedIdStr && empIdStr === assignedIdStr && (item.backendStatus === "available" || item.originalAdjustedTime)) {
          suffix = " (Frei)"
          textClass = "success--text font-weight-bold"
        } else if (item.alternatives && item.alternatives.some((alt) => alt.id && String(alt.id) === empIdStr)) {
          suffix = " (Frei)"
          textClass = "success--text font-weight-bold"
        } else {
          suffix = " (Kollision/Abwesend)"
          textClass = "grey--text"
        }

        return {
          text: (emp.name || emp.firstName || "Mitarbeiter") + suffix,
          value: empIdStr,
          class: textClass,
        }
      })
    },
    getPreviewItemStyle(item) {
      if (item.skipped) {
        return {
          borderColor: "rgba(244, 67, 54, 0.15)",
          background: "rgba(244, 67, 54, 0.03)",
        }
      }
      if (item.status === "collision") {
        return {
          borderColor: "rgba(255, 152, 0, 0.2)",
          background: "rgba(255, 152, 0, 0.03)",
        }
      }
      if (item.status === "no_shiftplan") {
        return {
          borderColor: "rgba(255, 193, 7, 0.2)",
          background: "rgba(255, 193, 7, 0.03)",
        }
      }
      if (item.status === "absent") {
        return {
          borderColor: "rgba(244, 67, 54, 0.15)",
          background: "rgba(244, 67, 54, 0.03)",
        }
      }
      return {}
    },
    formatPreviewDate(dateStr) {
      if (!dateStr) return ""
      return moment(dateStr, "YYYY-MM-DD").locale("de").format("dd. DD.MM.YYYY")
    },
    getCollisionTooltip(conflict) {
      if (!conflict) return ""
      return `Kollision mit: ${conflict.title || "Termin"} (${conflict.start} - ${conflict.end})`
    },
    updateCustomer(value) {
      this.selectedCustomer = value
    },
    updateCustomer2(value) {
      this.selectedCustomer2 = value
    },
    addSecondPerson() {
      this.showSecondPerson = true
      this.localDuration = this.baseDuration + 30
    },
    removeSecondPerson() {
      this.showSecondPerson = false
      this.selectedCustomer2 = null
      this.noAccountMode2 = false
      this.noAccountInfo2 = { firstName: "", lastName: "", phone: "", email: "" }
      this.localDuration = this.baseDuration
    },
    toggleFinder() {
      this.showFinder = !this.showFinder
    },
    formatDate(dateStr) {
      if (!dateStr) return ""
      return moment(dateStr, "YYYY-MM-DD").format("DD.MM.YYYY")
    },
    toggleWeekday(value) {
      const idx = this.recurrence.weekdays.indexOf(value)
      if (idx > -1) {
        if (this.recurrence.weekdays.length > 1) {
          this.recurrence.weekdays.splice(idx, 1)
        }
      } else {
        this.recurrence.weekdays.push(value)
      }
    },
    save() {
      if (this.isRecurring) {
        const nonSkippedItems = this.previewList.filter((item) => !item.skipped)
        if (!nonSkippedItems.length) {
          this.$toast.error("Bitte mindestens einen Termin in der Serie aktivieren.")
          return
        }

        const items = nonSkippedItems.map((item) => {
          const empId = item.selectedEmployeeId
          const isHelper = empId === "ma_info" || empId === "no_show" || empId === "unassigned"

          const empObj = this.employees.find((e) => String(e.id) === String(empId))
          const dbId = empObj ? empObj.dbId : null

          const itemTime = item.adjustedTime || this.localStart
          const endM = moment(`${item.date} ${itemTime}`, "YYYY-MM-DD HH:mm").add(this.localDuration, "minutes")

          if (this.data?.template?.type === "task") {
            return {
              date: item.date,
              time: itemTime,
              title: this.localTitle,
              description: this.localDesc,
              assignedEmployee: isHelper ? null : dbId,
              helperColumn: isHelper ? empId : "none",
              icon: this.localIcon,
              color: this.data.template.color,
              duration: this.localDuration,
              checklist: this.data.template.checklist ? JSON.parse(JSON.stringify(this.data.template.checklist)) : [],
              instructions: this.data.template.instructions || "",
              templateId: this.data.template._id || this.data.template.id,
            }
          } else {
            const apptData = {
              date: item.date,
              start: itemTime,
              end: endM.format("HH:mm"),
              title: this.localTitle,
              description: this.localDesc,
              assignedEmployee: isHelper ? null : dbId,
              helperColumn: isHelper ? empId : "none",
              customer: this.data?.template.requiresCustomer
                ? this.data?.template.specialType === "new_contract" && this.contractMode === "existing"
                  ? this.selectedCustomer
                  : this.noAccountMode
                  ? null
                  : this.selectedCustomer
                : null,
              customer2: this.showSecondPerson && !this.noAccountMode2 ? this.selectedCustomer2 : null,
              icon: this.localIcon,
              color: this.data.template.color,
              specialType: this.data.template.specialType || null,
              templateId: this.data.template._id || this.data.template.id,
            }

            if (this.noAccountMode && this.noAccountInfo.firstName) {
              apptData.noAccountData = { ...this.noAccountInfo }
            }
            if (this.showSecondPerson && this.noAccountMode2 && this.noAccountInfo2.firstName) {
              apptData.noAccountData2 = { ...this.noAccountInfo2 }
            }
            if (this.data?.template.specialType === "new_contract" && this.contractMode === "guest" && this.guestData.firstName) {
              apptData.rawData = { guestData: { ...this.guestData } }
            }
            return apptData
          }
        })

        this.$emit("save", {
          isBatch: true,
          items,
          type: this.data?.template?.type === "task" ? "task" : "appointment",
          extendSeriesId: this.data?.extendSeriesId || null,
        })
        return
      }

      const payload = {
        ...this.data,
        colId: this.localColId,
        time: this.localStart,
        duration: this.localDuration,
        description: this.localDesc,
        date: this.selectedDate || this.data?.date || this.currentDate,
        template: {
          ...this.data.template,
          title: this.localTitle,
          icon: this.localIcon,
        },
        customer:
          this.data?.template.specialType === "new_contract" && this.contractMode === "existing"
            ? this.selectedCustomer
            : this.noAccountMode
            ? null
            : this.selectedCustomer,
        noAccountData: this.noAccountMode ? { ...this.noAccountInfo } : null,
        guestData: this.data?.template.specialType === "new_contract" && this.contractMode === "guest" ? { ...this.guestData } : null,
        customer2: this.showSecondPerson && !this.noAccountMode2 ? this.selectedCustomer2 : null,
        noAccountData2: this.showSecondPerson && this.noAccountMode2 ? { ...this.noAccountInfo2 } : null,
        hasSecondPerson: this.showSecondPerson,
        recurrence: null,
      }
      this.$emit("save", payload)
    },
  },
}
</script>

<style scoped>
.wcd-card {
  background: rgba(22, 22, 22, 0.85) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4) !important;
}
.wcd-card.light-theme {
  background: rgba(255, 255, 255, 0.88) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
}
.wcd-card.light-theme .wcd-title {
  color: #1e293b !important;
}
.wcd-card.light-theme .wcd-subtitle {
  color: rgba(0, 0, 0, 0.6) !important;
}
.wcd-card.light-theme .wcd-time-label {
  color: rgba(0, 0, 0, 0.5) !important;
}
.wcd-card.light-theme .wcd-finder-toggle-bar {
  background: rgba(0, 0, 0, 0.04) !important;
  color: rgba(0, 0, 0, 0.7) !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.wcd-card.light-theme .wcd-finder-toggle-bar.active {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-color: #2e7d32 !important;
}

.wcd-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
}
.wcd-icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
.wcd-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: white;
  letter-spacing: -0.2px;
}
.wcd-subtitle {
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 1px;
}

/* Time Row */
.wcd-time-row {
  display: flex;
  gap: 12px;
}
.wcd-time-item {
  flex: 1;
}
.wcd-time-label {
  font-size: 0.65rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 6px;
}
.wcd-time-input >>> input {
  text-align: center;
  font-weight: 700;
  font-size: 0.9rem;
}
.wcd-time-input >>> .v-input__slot {
  border-radius: 10px !important;
  min-height: 38px !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  transition: all 0.25s;
}
.wcd-time-input >>> .v-input__slot:hover {
  border-color: rgba(255, 255, 255, 0.15) !important;
}
.light-theme .wcd-time-input >>> .v-input__slot {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.light-theme .wcd-time-input >>> .v-input__slot:hover {
  border-color: rgba(0, 0, 0, 0.18) !important;
}

.wcd-time-result {
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 800;
  color: #4caf50;
  background: rgba(76, 175, 80, 0.08);
  border-radius: 10px;
  border: 1px solid rgba(76, 175, 80, 0.15);
}

/* Toggle Buttons */
.wcd-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: none;
}
.wcd-card.light-theme .wcd-toggle-btn {
  color: rgba(0, 0, 0, 0.6);
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.wcd-toggle-btn.active {
  color: white !important;
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  border-color: #2e7d32 !important;
  box-shadow: 0 4px 10px rgba(76, 175, 80, 0.3) !important;
}
.wcd-card.light-theme .wcd-toggle-btn.active {
  color: white !important;
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  border-color: #2e7d32 !important;
  box-shadow: 0 4px 10px rgba(76, 175, 80, 0.3) !important;
}
.wcd-toggle-btn:hover {
  border-color: rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.04);
}
.wcd-card.light-theme .wcd-toggle-btn:hover {
  border-color: rgba(0, 0, 0, 0.15);
  background: rgba(0, 0, 0, 0.05);
}
.wcd-toggle-btn i {
  color: inherit !important;
}

/* Dual Person Badge */
.wcd-dual-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--v-primary-base) !important;
  background: color-mix(in srgb, var(--v-primary-base) 10%, transparent) !important;
  border: 1px solid color-mix(in srgb, var(--v-primary-base) 20%, transparent) !important;
}

/* Employee Dropdown Select */
.wcd-employee-select >>> .v-input__slot {
  border-radius: 10px !important;
  min-height: 38px !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  transition: all 0.25s;
}
.wcd-employee-select >>> .v-input__slot:hover {
  border-color: rgba(255, 255, 255, 0.15) !important;
}
.light-theme .wcd-employee-select >>> .v-input__slot {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.light-theme .wcd-employee-select >>> .v-input__slot:hover {
  border-color: rgba(0, 0, 0, 0.18) !important;
}
.wcd-employee-select >>> .v-select__selection {
  font-size: 0.85rem;
  font-weight: 600;
  color: inherit;
}

/* Save Button */
.wcd-save-btn {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 0.85rem !important;
  padding: 8px 16px !important;
  transition: all 0.2s !important;
}
.wcd-save-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.35) !important;
}

/* Recurrence Panel Styles */
.wcd-recurring-panel {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.3s;
}
.light-theme .wcd-recurring-panel {
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.05);
}
.wcd-small-label {
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 6px;
}
.light-theme .wcd-small-label {
  color: rgba(0, 0, 0, 0.5);
}
.wcd-toggle-group {
  background: transparent !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 10px !important;
  width: 100%;
  display: flex !important;
  overflow: hidden;
}
.light-theme .wcd-toggle-group {
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}
.wcd-toggle-btn-small {
  flex: 1;
  text-transform: none !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  letter-spacing: 0px !important;
  background: transparent !important;
  border: none !important;
  height: 32px !important;
  color: rgba(255, 255, 255, 0.55) !important;
}
.light-theme .wcd-toggle-btn-small {
  color: rgba(0, 0, 0, 0.6) !important;
}
.wcd-toggle-group >>> .v-btn--active {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
}
.light-theme .wcd-toggle-group >>> .v-btn--active {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
}
.wcd-numeric-input >>> input {
  text-align: center;
  font-weight: 700;
}
.wcd-numeric-input >>> .v-input__slot {
  border-radius: 8px !important;
  min-height: 34px !important;
  padding: 0 8px !important;
}
.wcd-date-input >>> .v-input__slot {
  border-radius: 8px !important;
  min-height: 34px !important;
}
.wcd-date-input >>> input {
  font-weight: 600;
  font-size: 0.8rem;
}
.wcd-radio-group >>> .v-radio {
  margin-bottom: 0px !important;
}
.overflow-visible {
  overflow: visible !important;
}
.gap-2 {
  gap: 8px;
}
.wcd-preview-item {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.25s ease;
}
.wcd-preview-item.light-theme-item {
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.05);
}
.wcd-preview-item.skipped {
  opacity: 0.6;
  background: rgba(244, 67, 54, 0.02) !important;
  border-color: rgba(244, 67, 54, 0.1) !important;
}
.wcd-preview-employee-select >>> .v-input__slot {
  border-radius: 8px !important;
  min-height: 28px !important;
  padding: 0 8px !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
}
.light-theme .wcd-preview-employee-select >>> .v-input__slot {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.wcd-preview-employee-select >>> .v-select__selection {
  font-size: 0.75rem;
  font-weight: 500;
}
</style>
